import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Snackbar,
  Select,
  MenuItem,
  Button,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { SortIconDropdown, SortOption } from '@/components/SortIconDropdown/SortIconDropdown';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  DistributionGroup,
  CreateDistributionGroupInput,
  UpdateDistributionGroupInput
} from '@/types/distributionGroupTypes';
import {
  getDistributionGroups,
  createDistributionGroup,
  updateDistributionGroup,
  deleteDistributionGroup
} from '@/services/distributionGroupService';
import { DistributionGroupDialog } from './DistributionGroupDialog';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface DistributionGroupListProps {
  companyId: string;
  triggerCreate?: number;
}

export const DistributionGroupList: React.FC<DistributionGroupListProps> = ({
  companyId,
  triggerCreate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<DistributionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DistributionGroup | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<DistributionGroup | null>(
    null
  );

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'recipientCount'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Sort options for the dropdown
  const sortOptions: SortOption[] = [
    { key: 'name', label: 'Name' },
    { key: 'recipientCount', label: 'Recipients' }
  ];

  // Loading states for individual actions

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  const fetchGroups = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    clearMessages();

    try {
      const fetchedGroups = await getDistributionGroups(
        companyId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setGroups(fetchedGroups);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch distribution groups');
      console.error('Error fetching distribution groups:', err);
    } finally {
      setIsLoading(false);
    }
  }, [
    companyId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    clearMessages
  ]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Watch for trigger to create new group
  useEffect(() => {
    if (triggerCreate && triggerCreate > 0) {
      setEditingGroup(null);
      setDialogOpen(true);
    }
  }, [triggerCreate]);

  const handleCreateGroup = async (input: CreateDistributionGroupInput) => {
    try {
      clearMessages();
      const newGroup = await createDistributionGroup(
        input,
        companyId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setGroups((prev) => [...prev, newGroup]);
      setSuccessMessage(
        `Distribution group "${newGroup.name}" created successfully`
      );
      setDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create distribution group');
      console.error('Error creating distribution group:', err);
    }
  };

  const handleUpdateGroup = async (input: UpdateDistributionGroupInput) => {
    try {
      clearMessages();
      const updatedGroup = await updateDistributionGroup(
        input,
        companyId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setGroups((prev) =>
        prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
      );
      setSuccessMessage(
        `Distribution group "${updatedGroup.name}" updated successfully`
      );
      setDialogOpen(false);
      setEditingGroup(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update distribution group');
      console.error('Error updating distribution group:', err);
    }
  };

  const handleDeleteGroup = async (group: DistributionGroup) => {
    try {
      clearMessages();
      await deleteDistributionGroup(
        { id: group.id },
        companyId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      setSuccessMessage(
        `Distribution group "${group.name}" deleted successfully`
      );
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete distribution group');
      console.error('Error deleting distribution group:', err);
    }
  };

  // Apply search filter
  let filteredGroups = groups.filter(
    (group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply status filter
  if (statusFilter !== 'all') {
    filteredGroups = filteredGroups.filter((group) =>
      statusFilter === 'active' ? group.isActive : !group.isActive
    );
  }

  // Apply sorting
  filteredGroups.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    if (sortBy === 'name') {
      aValue = a.name;
      bValue = b.name;
    } else {
      aValue = a.emailAddresses.length;
      bValue = b.emailAddresses.length;
    }

    let comparison: number;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue);
    } else {
      comparison = (aValue as number) - (bValue as number);
    }

    return sortDir === 'asc' ? comparison : -comparison;
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 3, py: 3, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
        {/* Error Alert - Keep at top for errors */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearMessages}>
            {error}
          </Alert>
        )}

        {isLoading && groups.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <>
            {groups.length > 0 ? (
              <>
                {/* Search and Sort Row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2
                  }}
                >
                  <TextField
                    placeholder="Search distribution groups..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        height: 40,
                        '&:hover': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
                        },
                        '&.Mui-focused': {
                          borderColor: '#3b82f6',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: theme.palette.text.secondary, fontSize: '18px' }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchTerm('')}
                            edge="end"
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <SortIconDropdown
                    options={sortOptions}
                    currentSort={sortBy}
                    currentDirection={sortDir}
                    onSortChange={(key, direction) => {
                      if (key && direction) {
                        setSortBy(key as 'name' | 'recipientCount');
                        setSortDir(direction);
                      }
                    }}
                  />

                  <Button
                    onClick={() => {
                      setShowFilters(!showFilters);
                      if (!showFilters) {
                        if (statusFilter !== 'all') {
                          setStatusFilter('all');
                        }
                      } else {
                        setStatusFilter('all');
                      }
                    }}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      height: 40,
                      width: 40,
                      minWidth: 40,
                      px: 0,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: showFilters
                        ? 'rgba(75, 94, 219, 0.04)'
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white'),
                      color: showFilters ? '#4B5EDB' : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: showFilters
                          ? 'rgba(75, 94, 219, 0.08)'
                          : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f8f9fa'),
                        borderColor: showFilters ? '#4B5EDB' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db')
                      }
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: '18px' }} />
                  </Button>
                </Box>

                {/* Filter Pills - Only show when filters are enabled */}
                {showFilters && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {[
                      { key: 'all', label: 'All', count: groups.length },
                      {
                        key: 'active',
                        label: 'Active',
                        count: groups.filter((g) => g.isActive).length
                      },
                      {
                        key: 'inactive',
                        label: 'Inactive',
                        count: groups.filter((g) => !g.isActive).length
                      }
                    ].map((filter) => (
                      <Button
                        key={filter.key}
                        onClick={() => setStatusFilter(filter.key)}
                        sx={{
                          px: 2.5,
                          py: 0.75,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '13px',
                          fontWeight: 500,
                          minHeight: 32,
                          background: statusFilter === filter.key 
                            ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#1f2937')
                            : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'),
                          color: statusFilter === filter.key ? 'white' : theme.palette.text.secondary,
                          border: 'none',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            background: statusFilter === filter.key 
                              ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#111827')
                              : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb')
                          }
                        }}
                      >
                        {filter.label} ({filter.count})
                      </Button>
                    ))}
                  </Box>
                )}

                {/* Groups List */}
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {filteredGroups.map((group) => (
                    <Card
                      key={group.id}
                      sx={{
                        background: theme.palette.background.paper,
                        borderRadius: '6px',
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                        }
                      }}
                      onClick={(e) => {
                        // Don't navigate if clicking on action button
                        if ((e.target as HTMLElement).closest('.action-button')) {
                          return;
                        }
                        navigate(`/distribution-groups/${group.id}`);
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        {/* Table-like layout */}
                        <Box
                          sx={{
                            display: { xs: 'flex', sm: 'grid' },
                            flexDirection: { xs: 'row', sm: 'unset' },
                            gridTemplateColumns: {
                              sm: '2.5fr 120px 120px auto'
                            },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2,
                            minHeight: { xs: 'auto', sm: 48 }
                          }}
                        >
                          {/* Group Name Column */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              minWidth: 0,
                              flex: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 600,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
                                color: '#6b7280',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <EmailIcon sx={{ fontSize: '16px' }} />
                            </Box>

                            <Box
                              sx={{
                                minWidth: 0,
                                flex: 1
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    color: theme.palette.text.primary,
                                    textAlign: 'left',
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {group.name}
                                </Typography>

                                {/* Status Chip - Mobile inline */}
                                {isMobile && (
                                  <Chip
                                    label={group.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                      height: '20px',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                      borderRadius: '4px',
                                      backgroundColor: group.isActive
                                        ? theme.palette.mode === 'dark'
                                          ? 'rgba(34, 197, 94, 0.15)'
                                          : 'rgba(34, 197, 94, 0.1)'
                                        : theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : '#f3f4f6',
                                      color: group.isActive ? '#22c55e' : theme.palette.text.secondary,
                                      border: 'none',
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                )}
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: theme.palette.text.secondary,
                                  textAlign: 'left',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {group.emailAddresses.length} recipient{group.emailAddresses.length !== 1 ? 's' : ''}
                                {group.description && ` • ${group.description}`}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Recipients Column - Desktop only */}
                          <Box
                            sx={{
                              display: { xs: 'none', sm: 'flex' },
                              alignItems: 'center'
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '12px' }}
                            >
                              {group.emailAddresses.length}{' '}
                              {group.emailAddresses.length === 1 ? 'recipient' : 'recipients'}
                            </Typography>
                          </Box>

                          {/* Status Column - Desktop only */}
                          <Box
                            sx={{
                              display: { xs: 'none', sm: 'flex' },
                              alignItems: 'center',
                              gap: 0.75
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: group.isActive
                                  ? 'success.main'
                                  : 'text.disabled'
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                fontWeight: 500
                              }}
                            >
                              {group.isActive ? 'Active' : 'Inactive'}
                            </Typography>
                          </Box>

                          {/* Actions Column */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              gap: 0.5,
                              flexShrink: 0
                            }}
                          >
                            <IconButton
                              size="small"
                              className="action-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/distribution-groups/${group.id}`);
                              }}
                              sx={{
                                borderRadius: '6px',
                                color: theme.palette.text.secondary,
                                mt: { xs: 0.25, sm: 0 },
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : '#f3f4f6',
                                  color: theme.palette.text.primary
                                }
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography
                  variant="h6"
                  sx={{ color: theme.palette.text.secondary, mb: 1, fontWeight: 500 }}
                >
                  No distribution groups yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, opacity: 0.7, maxWidth: 300, mx: 'auto' }}
                >
                  Create distribution groups to share summaries with specific
                  audiences.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Dialogs */}
        <DistributionGroupDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditingGroup(null);
          }}
          onSubmit={async (input) => {
            if (editingGroup) {
              await handleUpdateGroup(input as UpdateDistributionGroupInput);
            } else {
              await handleCreateGroup(input as CreateDistributionGroupInput);
            }
          }}
          editingGroup={editingGroup}
        />

        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
          }}
          onConfirm={() => groupToDelete && handleDeleteGroup(groupToDelete)}
          groupName={groupToDelete?.name || ''}
        />
      </Box>

      {/* Success Toast Notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={clearMessages}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#10b981',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
        message={successMessage}
      />
    </>
  );
};
